import { startCase } from 'lodash'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  CheckboxInputStyle,
  UncontrolledRadioInput,
} from '@src/components/Inputs'
import { SectionHeader } from '@src/components/ServicePopover/SectionHeader'
import { BottomSheet } from '@src/components/BottomSheet/BottomSheet'
import {
  MobileNavigationPopoverContentWrapper,
  MobileNavigationPopoverContent,
  MobileNavigationPopoverFooter,
  MobileNavigationPopoverFooterContent,
  MobileNavigationResetLink,
  DoneButton,
  OptionContentLabels,
  OptionContentSubTitle,
  OptionContentTitle,
  OptionLabel,
  PopoverOptionContainer,
  SectionContentContainer,
} from '@src/components/BottomSheet/BottomSheet.styles'
import { SharedAddress } from '@src/hooks/sharedQueries/useCustomerDetailsAndAddressesQuery/useCustomerDetailsAndAddressesQuery'
import {
  AddressesChildName,
  MainRouteName,
  useAccountRouter,
} from '@src/hooks/useAccountRouter'
import { useBottomSheetStack } from '../../hooks/useBottomSheetStack/useBottomSheetStack'

export const AddressesPopover: React.FC<{
  onChange?: (addressId: string) => void
  onDone: (addressId: string) => void
  selectedAddressId?: string | null
  addresses: SharedAddress[]
  // required to close the parent when navigating to add address
  handleCancelWhenNavigatingAway: () => void
  isPopover: boolean
}> = ({
  onChange,
  onDone,
  selectedAddressId = null,
  addresses,
  handleCancelWhenNavigatingAway,
  isPopover,
}) => {
  const {
    addressPickerBottomSheetController,
    serviceComponentBottomSheetController,
  } = useBottomSheetStack()
  const { t } = useTranslation('serviceNavigation')
  const [addressId, setAddressId] = useState(selectedAddressId)
  const { setRoute } = useAccountRouter()

  // if state is being managed by parent, update local state when parent state changes
  useEffect(() => {
    setAddressId(selectedAddressId)
  }, [selectedAddressId])

  const handleClose = (addAddress?: boolean) => {
    // reset address id to one from before any state change in case of immediate re-open
    setAddressId(selectedAddressId)
    // close the BottomSheet below
    addressPickerBottomSheetController.close()
    addAddress && serviceComponentBottomSheetController.close()
  }

  return (
    <BottomSheet
      bottomSheetController={addressPickerBottomSheetController}
      maxWidth={584}
      showBackdrop={!isPopover}
      onClose={() => setAddressId(selectedAddressId)}
    >
      <MobileNavigationPopoverContentWrapper>
        <MobileNavigationPopoverContent>
          <SectionHeader
            header={t('select_saved_address')}
            subheader={t('choose_from_saved_addresses')}
          />
          <SectionContentContainer>
            {addresses.map(
              ({
                id,
                name,
                firstLine,
                secondLine,
                thirdLine,
                city,
                postcode,
                acceptsDelivery,
              }) => (
                <PopoverOptionContainer key={id}>
                  <UncontrolledRadioInput
                    label={
                      <>
                        <OptionLabel>
                          <OptionContentLabels>
                            <OptionContentTitle>
                              {name ? startCase(name) : ''}
                            </OptionContentTitle>
                            <OptionContentSubTitle disabled={!acceptsDelivery}>
                              {acceptsDelivery
                                ? [
                                    startCase(firstLine),
                                    startCase(secondLine || ''),
                                    startCase(thirdLine || ''),
                                    startCase(city || ''),
                                    postcode,
                                  ]
                                    .filter(Boolean)
                                    .join(', ')
                                : t('delivery_not_available', {
                                    locationType: t('address').toLowerCase(),
                                  })}
                            </OptionContentSubTitle>
                          </OptionContentLabels>
                        </OptionLabel>
                      </>
                    }
                    style={CheckboxInputStyle.TICK}
                    disabled={!acceptsDelivery}
                    checked={addressId === id}
                    onClick={e => {
                      e?.stopPropagation()
                      onChange ? onChange(id) : setAddressId(id)
                    }}
                    dataTestId={`service-wherepopover-radio-${name.toLowerCase()}`}
                  />
                </PopoverOptionContainer>
              )
            )}

            <br />
            <PopoverOptionContainer>
              <UncontrolledRadioInput
                dataTestId="service-wherepopover-radio-add-address"
                label={
                  <OptionContentTitle>{t('add_address')}</OptionContentTitle>
                }
                style={CheckboxInputStyle.ADD}
                checked={false}
                onClick={e => {
                  e?.stopPropagation()
                  handleClose(true)
                  handleCancelWhenNavigatingAway()
                  setRoute({
                    mainRouteName: MainRouteName.ADDRESSES,
                    childRouteName: AddressesChildName.ADD,
                  })
                }}
              />
            </PopoverOptionContainer>
          </SectionContentContainer>
        </MobileNavigationPopoverContent>
      </MobileNavigationPopoverContentWrapper>
      <MobileNavigationPopoverFooter>
        <MobileNavigationPopoverFooterContent>
          <MobileNavigationResetLink
            onClick={e => {
              e?.stopPropagation()
              handleClose()
            }}
          >
            {t('cancel')}
          </MobileNavigationResetLink>

          <DoneButton
            content={t('done')}
            width="180px"
            onClick={e => {
              e?.stopPropagation()
              addressId && onDone(addressId)
              addressPickerBottomSheetController.close()
            }}
            type={'button'}
            disabled={!addressId}
            dataTestId="menu-addressbook-button-done"
          />
        </MobileNavigationPopoverFooterContent>
      </MobileNavigationPopoverFooter>
    </BottomSheet>
  )
}
